import { createTheme as createMuiTheme, responsiveFontSizes, type Theme } from '@mui/material/styles'
import { type SpacingArgument } from '@mui/system/createTheme/createSpacing'
import { rem } from 'polished'

import { overridesFormHelperText } from '@Material/form'
import { overridesSnackbar } from '@Material/snackbar'
import { RADIUS_1 } from '@Styles/constants/radius'
import canUseDom from '@Utils/can-use-dom'
import { remToNumberOfPixels } from '@Utils/units'

import { overridesAccordion, overridesAccordionDetails, overridesAccordionSummary } from './accordion'
import { overridesAppBar } from './appBar'
import { overridesBackdrop } from './backdrop'
import { overridesBottomNavigation, overridesBottomNavigationAction } from './bottomNavigation'
import { overridesButtonBase } from './button'
import { overridesCard, overridesCardActionArea, overridesCardContent } from './card'
import { overridesCheckbox } from './checkbox'
import { overridesCssBaseLine } from './cssbaseline'
import { customPalette } from './customPalette'
import { overridesDialog, overridesDialogActions, overridesDialogContent } from './dialog'
import { overridesDivider } from './divider'
import { overridesDrawer } from './drawer'
import { overridesFormLabel } from './formLabel'
import { overridesIconButton } from './iconButton'
import { overridesInputLabel } from './inputLabel'
import { overridesListItemButton } from './listItemButton'
import { overridesMenuItem } from './menuItem'
import { overridesOutlinedInput } from './outlinedInput'
import { palette } from './palette'
import { overridesRadio } from './radio'
import { overridesSelect } from './select'
import { overridesSkeleton } from './skeleton'
import { overridesSlider } from './slider'
import { overridesSwitch } from './switch'
import { overridesTab, overridesTabs } from './tabs'
import { overridesTextField } from './textField'
import { overridesTooltip } from './tooltip'
import { globalTypography, typographyOverrides as overridesTypography } from './typography'

export const SPACING_BASE = 8

export const createTheme = (prefersDarkMode: boolean): Theme => {
  const baseTheme = createMuiTheme({
    palette: palette(prefersDarkMode),
    ...customPalette(prefersDarkMode),
    typography: globalTypography,
    shadows: [
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
      'none',
    ],
    shape: {
      borderRadius: remToNumberOfPixels(RADIUS_1),
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768,
        lg: 1024,
        xl: 1440,
      },
    },
    /**
     * According to DS strategy
     * @see https://www.figma.com/design/mnXJEBUvdu3P0mF1vIYPrO/Grid-%26-Spacing-DS?node-id=3-10634&t=MpguPK15cX6w2ggG-4
     * @example
     * theme.spacing(0); // = 4px
     * theme.spacing(1); // = 8px
     * theme.spacing(2); // = 16px
     * theme.spacing(3); // = 24px
     * theme.spacing(4); // = 32px
     * theme.spacing(5); // = 40px
     * theme.spacing(6); // = 48px
     * theme.spacing(7); // = 56px
     * theme.spacing(8); // = 64px
     * …
     * @example
     * theme.spacing(-0); // ≠ -4px so prefer `-${theme.spacing(0)}` for negative value
     */
    spacing: (factor: SpacingArgument) => {
      if (typeof factor !== 'number') {
        return factor
      }

      return factor === 0 ? rem(SPACING_BASE / 2) : rem(SPACING_BASE * factor)
    },
  })

  const theme = createMuiTheme(baseTheme, {
    components: {
      MuiAccordion: {
        defaultProps: {
          slotProps: {
            heading: { component: 'div' },
          },
        },
        styleOverrides: overridesAccordion(),
      },
      MuiAccordionSummary: {
        styleOverrides: overridesAccordionSummary(baseTheme),
      },
      MuiAccordionDetails: {
        styleOverrides: overridesAccordionDetails(),
      },
      MuiAppBar: {
        styleOverrides: overridesAppBar(baseTheme),
      },
      MuiBackdrop: {
        styleOverrides: overridesBackdrop(baseTheme),
      },
      MuiBottomNavigation: {
        styleOverrides: overridesBottomNavigation(baseTheme),
      },
      MuiBottomNavigationAction: {
        styleOverrides: overridesBottomNavigationAction(baseTheme),
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: overridesButtonBase(baseTheme),
      },
      MuiIconButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: overridesIconButton(),
      },
      MuiCard: {
        styleOverrides: overridesCard(),
      },
      MuiCardActionArea: {
        styleOverrides: overridesCardActionArea(baseTheme),
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiCardContent: {
        styleOverrides: overridesCardContent(baseTheme),
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: overridesCheckbox(baseTheme),
      },
      MuiCssBaseline: {
        styleOverrides: overridesCssBaseLine(),
      },
      MuiDialog: {
        styleOverrides: overridesDialog(),
      },
      MuiDialogActions: {
        styleOverrides: overridesDialogActions(baseTheme),
      },
      MuiDialogContent: {
        styleOverrides: overridesDialogContent(),
      },
      MuiDivider: {
        styleOverrides: overridesDivider(),
      },
      MuiDrawer: {
        styleOverrides: overridesDrawer(),
      },
      MuiInputLabel: {
        styleOverrides: overridesInputLabel(baseTheme),
      },
      MuiFormLabel: {
        styleOverrides: overridesFormLabel(baseTheme),
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
      },
      MuiListItemButton: {
        styleOverrides: overridesListItemButton(baseTheme),
      },
      MuiMenuItem: {
        styleOverrides: overridesMenuItem(baseTheme),
      },
      MuiOutlinedInput: {
        styleOverrides: overridesOutlinedInput(baseTheme),
      },
      MuiRadio: {
        styleOverrides: overridesRadio(baseTheme),
        defaultProps: {
          color: 'primary',
          disableRipple: true,
        },
      },
      MuiSelect: {
        defaultProps: {
          SelectDisplayProps: {
            role: 'button',
          },
        },
        styleOverrides: overridesSelect(),
      },
      MuiSkeleton: {
        styleOverrides: overridesSkeleton(),
      },
      MuiSlider: {
        styleOverrides: overridesSlider(baseTheme),
      },
      MuiSwitch: {
        styleOverrides: overridesSwitch(baseTheme),
      },
      MuiTab: {
        styleOverrides: overridesTab(baseTheme),
      },
      MuiTabs: {
        styleOverrides: overridesTabs(baseTheme),
      },
      MuiTextField: {
        styleOverrides: overridesTextField(),
      },
      MuiTooltip: {
        styleOverrides: overridesTooltip(baseTheme),
      },
      MuiSnackbar: {
        styleOverrides: overridesSnackbar(baseTheme),
      },
      MuiFormHelperText: {
        styleOverrides: overridesFormHelperText(baseTheme),
      },
      MuiTypography: {
        defaultProps: {
          variant: 'body2',
          variantMapping: {
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
            subtitle1: 'p',
            subtitle2: 'p',
            body1: 'p',
            body2: 'p',
          },
        },
        styleOverrides: overridesTypography(baseTheme),
      },
    },
  })

  if (canUseDom) {
    window.theme = theme
  }

  return responsiveFontSizes(theme)
}
